import { defineStore } from "pinia";
import { $axios } from "@bloglovin/vue-framework";
import { IMPORT_STATUS_POLL_INTERVAL_MS } from "@/constants/social-network-constants";
import { socialNetworkConnectionStatus } from "@/enums/social-network-enums";
import { apiErrorMessage } from "@/helpers/social-network-status-helper";

export const MSG_LOAD_FAILED = "We couldn't load your connections. Refresh the page to try again.";
export const MSG_CONNECT_FAILED = "We couldn't start the connection. Try again in a moment.";
export const MSG_DISCONNECT_FAILED = "We couldn't disconnect this account. Try again in a moment.";
export const MSG_MANUAL_CONNECT_FAILED = "We couldn't save this connection. Check the details and try again.";
export const MSG_LINKED_PUBLICATIONS_FAILED = "We couldn't check where this account is being used. Try again in a moment.";

export const useSocialNetworksStore = defineStore("social-networks-store", {
  state: () => ({
    socialNetworks: [],
    isImportingRecentPosts: false,
    connectResult: null,
    pageError: "",
    pendingAccountIds: [],
    accountErrors: {},
    credentialConnectError: "",
    isCredentialConnecting: false,
    linkedPublications: [],
    isLoadingLinkedPublications: false,
    hasLinkedPublicationsError: false,
  }),
  getters: {
    isAccountPending: state => accountId => state.pendingAccountIds.includes(accountId),
  },
  actions: {
    loadSocialNetworks(orgId) {
      this.pageError = "";
      this.apiGet("/social-network/accounts", { org_id: orgId }, data => {
        this.socialNetworks = data.social_networks;
      }, error => {
        this.pageError = apiErrorMessage(error, MSG_LOAD_FAILED);
      });
    },
    loadSocialNetworksForPublication(publicationId) {
      this.pageError = "";
      this.apiGet(`/social-network/publication/${publicationId}/account-statuses`, {}, data => {
        this.socialNetworks = data.social_networks;
      }, error => {
        this.pageError = apiErrorMessage(error, MSG_LOAD_FAILED);
      });
    },
    setSocialNetworks(socialNetworks) {
      this.socialNetworks = socialNetworks;
    },
    setConnectResult(connectResult) {
      this.connectResult = connectResult;
    },
    dismissConnectResult() {
      this.connectResult = null;
    },
    dismissPageError() {
      this.pageError = "";
    },
    connect(socialNetworkName, orgId) {
      this.pageError = "";
      return $axios.post("/social-network/connect", {
        social_network_name: socialNetworkName,
        redirect_uri: `${window.blclient.data.links.web}/social-network/connect?org_id=${orgId}&social_network=${socialNetworkName}`,
      }).then(response => {
        window.open(`${response.data.uri}`, "_self");
      }).catch(error => {
        this.pageError = apiErrorMessage(error, MSG_CONNECT_FAILED);
      });
    },
    manualConnect(socialNetworkName, orgId, accountHandle, appPassword = "") {
      this.pageError = "";
      this.credentialConnectError = "";
      this.isCredentialConnecting = true;
      return $axios.post("/social-network/manual-connect", {
        org_id: orgId,
        social_network_name: socialNetworkName,
        social_network_account_handle: accountHandle,
        app_password: appPassword,
      }).then(response => {
        this.socialNetworks = response.data.social_networks;
        return true;
      }).catch(error => {
        this.credentialConnectError = apiErrorMessage(error, MSG_MANUAL_CONNECT_FAILED);
        return false;
      }).finally(() => {
        this.isCredentialConnecting = false;
      });
    },
    clearCredentialConnectError() {
      this.credentialConnectError = "";
    },
    loadLinkedPublications(accountId) {
      this.linkedPublications = [];
      this.hasLinkedPublicationsError = false;
      this.isLoadingLinkedPublications = true;
      return this.apiGet(`/social-network/account/${accountId}/publications`, {}, data => {
        this.linkedPublications = data.publications;
        this.isLoadingLinkedPublications = false;
      }, error => {
        this.isLoadingLinkedPublications = false;
        this.hasLinkedPublicationsError = true;
        this.accountErrors = {
          ...this.accountErrors,
          [accountId]: apiErrorMessage(error, MSG_LINKED_PUBLICATIONS_FAILED),
        };
      });
    },
    clearLinkedPublications() {
      this.linkedPublications = [];
      this.hasLinkedPublicationsError = false;
    },
    disconnect(account, orgId) {
      const accountId = account.social_network_account_id;
      const endpoint = account.status === socialNetworkConnectionStatus.MANUAL_CONNECTION
        ? "/social-network/manual-disconnect"
        : "/social-network/disconnect";

      this.pendingAccountIds = [...this.pendingAccountIds, accountId];
      this.accountErrors = { ...this.accountErrors, [accountId]: "" };

      return $axios.post(endpoint, {
        social_network_name: account.social_network_name,
        org_id: orgId,
      }).then(response => {
        this.socialNetworks = response.data.social_networks;
      }).catch(error => {
        this.accountErrors = { ...this.accountErrors, [accountId]: apiErrorMessage(error, MSG_DISCONNECT_FAILED) };
      }).finally(() => {
        this.pendingAccountIds = this.pendingAccountIds.filter(id => id !== accountId);
      });
    },
    importRecentPosts(orgId, socialNetworkName, successHandler = () => {
    }, errorHandler = () => {
    }) {
      this.isImportingRecentPosts = true;
      this.apiPost("/social-network/import-recent-posts",
        {
          org_id: orgId,
          social_network_name: socialNetworkName,
        },
        data => {
          const importJobId = data.import_job_id;
          if (!importJobId) {
            this.isImportingRecentPosts = false;
            errorHandler();
            return;
          }
          this.pollImportStatus(importJobId, successHandler, errorHandler);
        },
        () => {
          this.isImportingRecentPosts = false;
          errorHandler();
        });
    },
    pollImportStatus(importJobId, successHandler = () => {
    }, errorHandler = () => {
    }) {
      let lastImportedCount = 0;
      const pollId = setInterval(() => {
        this.apiGet(`/social-network/import-status/${importJobId}`, {}, data => {
          if (data.imported_count > lastImportedCount) {
            lastImportedCount = data.imported_count;
            successHandler(data);
          }
          if (data.status === "complete") {
            clearInterval(pollId);
            this.isImportingRecentPosts = false;
            successHandler(data);
          } else if (data.status === "failed") {
            clearInterval(pollId);
            this.isImportingRecentPosts = false;
            errorHandler(data);
          }
        }, () => {
          clearInterval(pollId);
          this.isImportingRecentPosts = false;
          errorHandler();
        });
      }, IMPORT_STATUS_POLL_INTERVAL_MS);
    },
    postMessage(orgId, socialNetwork, postId, successCallback = () => {
    }, errorCallback = () => {
    }) {
      const formData = new FormData();
      formData.append("org_id", parseInt(orgId));
      formData.append("social_network_name", socialNetwork);
      formData.append("post_id", postId);

      $axios.post("/social-network/post",
        formData,
        {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        }).then(response => {
        successCallback(response.data);
      }).catch(error => {
        errorCallback(error);
      });
    },
  },
});
